import React from 'react';
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Checkbox,
  Grid,
  Typography,
  FormHelperText,
  Box,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import type { AddMaterialFormValues } from './types';
import { getUnitsForType, getDefaultUnit } from './validation';

function getUnitLabel(unit: string): string {
  if (unit === 'g') return 'gm';
  if (unit === 'µg') return 'ug';
  return unit;
}

export function QuantitySpecForm() {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AddMaterialFormValues>();

  const isEmpty = watch('isEmpty');
  const quantityType = watch('quantityType');

  const availableUnits = getUnitsForType(quantityType);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '170px 1fr',
        columnGap: '10px',
        alignItems: 'start',
      }}
    >
      {/* Section title */}
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 600,
          textAlign: 'right',
          pt: '10px',
        }}
      >
        Specify Quantity
      </Typography>

      {/* Quantity fields */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          columnGap: '26px',
        }}
      >
        {/* Quantity Type */}
        <Box>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#555',
              mb: '4px',
            }}
          >
            Quantity Type
            <Box
              component="span"
              sx={{ color: '#dc3545', ml: '2px' }}
            >
              *
            </Box>
          </Typography>

          <Controller
            name="quantityType"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                size="small"
                error={!!errors.quantityType}
              >
                <Select
                  {...field}
                  displayEmpty
                  onChange={(e) => {
                    field.onChange(e);
                    setValue(
                      'units',
                      getDefaultUnit(e.target.value),
                    );
                  }}
                >
                  <MenuItem value="Volume">Volume</MenuItem>
                  <MenuItem value="Weight">Weight</MenuItem>
                  <MenuItem value="Count">Count</MenuItem>
                </Select>

                {errors.quantityType && (
                  <FormHelperText>
                    {errors.quantityType.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>

        {/* Amount + Unit */}
        <Box>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#555',
              mb: '4px',
            }}
          >
            Amount
            <Box
              component="span"
              sx={{ color: '#dc3545', ml: '2px' }}
            >
              *
            </Box>
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 100px',
            }}
          >
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  size="small"
                  fullWidth
                  placeholder="Value"
                  disabled={isEmpty}
                  error={!!errors.amount}
                  helperText={errors.amount?.message}
                  slotProps={{
                    htmlInput: {
                      min: 0,
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    },
                  }}
                />
              )}
            />

            <Controller
              name="units"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.units}
                >
                  <Select
                    {...field}
                    disabled={isEmpty}
                    displayEmpty
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderLeft: 0,
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    {availableUnits.map((u) => (
                      <MenuItem key={u} value={u}>
                        {getUnitLabel(u)}
                      </MenuItem>
                    ))}
                  </Select>

                  {errors.units && (
                    <FormHelperText>
                      {errors.units.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Box>

          {/* Add Empty */}
          <Controller
            name="isEmpty"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                sx={{
                  mt: '2px',
                  ml: 0,
                  mb: 0,
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);

                      if (e.target.checked) {
                        setValue('amount', 0);
                      }
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px' }}>
                    Add Empty
                  </Typography>
                }
              />
            )}
          />
        </Box>
      </Box>
    </Box>
  );
}